import {
  CompRef,
  DocumentServicesObject,
  DsItem,
} from '@wix/document-services-types';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotToMoveContent,
  PluginsHostApiContext,
} from '@wix/editor-platform-host-integration-apis';
import { find, isEqual } from 'lodash';
import { platformEvents } from '../../../../platformEvents';
import {
  getPluginInfo,
  getPluginMarketData,
  virtualSlotPlaceholderSeparator,
} from '../utils/pluginUtils';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { tpaHostAPI } from './tpaHostAPI';
import { blocksHostAPI } from './blocksHostAPI';
import { SUPPORTED_PLUGIN_COMPONENT_TYPES } from '../constants';
import { PlatformContext } from '../../../../types/platformApi';
import { createPluginManager } from './pluginManager';
import { SitePluginsHostIntegrationApi } from '../types';

export const resolveHostAPIBySlotRef = (slotPlaceholderCompRef: CompRef) => {
  return slotPlaceholderCompRef.id.includes(virtualSlotPlaceholderSeparator)
    ? tpaHostAPI
    : blocksHostAPI;
};

export const getHostApi = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): SitePluginsHostIntegrationApi => ({
  resolveHostAPIByWidgetCompRef(widgetCompRef: CompRef) {
    const isTpa = documentServicesAPI.tpa.isTpaByCompType(
      this.getComponentType(widgetCompRef),
    );
    return isTpa ? tpaHostAPI : blocksHostAPI;
  },
  resolveHostAPIByWidgetPluginRef(widgetPluginCompRef: CompRef) {
    return blocksHostAPI.isPluginComponent(
      documentServicesAPI,
      widgetPluginCompRef,
    )
      ? blocksHostAPI
      : tpaHostAPI;
  },
  getComponentType(compRef: CompRef) {
    return documentServicesAPI.components.getType(compRef);
  },
  getWidgetSlot(slotCompRef: CompRef) {
    return resolveHostAPIBySlotRef(slotCompRef).getWidgetSlot(
      documentServicesAPI,
      slotCompRef,
    );
  },
  getWidgetSlots(widgetCompRef: CompRef) {
    return this.resolveHostAPIByWidgetCompRef(widgetCompRef).getWidgetSlots(
      documentServicesAPI,
      widgetCompRef,
    );
  },
  getWidgetSlotPlacement(slotComponentRef: CompRef): WidgetSlot['placement'] {
    return resolveHostAPIBySlotRef(slotComponentRef).getWidgetSlot(
      documentServicesAPI,
      slotComponentRef,
    )?.placement;
  },
  async installWidgetPlugin(
    context: PlatformContext,
    slotComponentRef: CompRef,
    widgetPointer: WidgetPointer,
    pluginsHostAPIContext: PluginsHostApiContext,
  ): Promise<CompRef> {
    const manager = createPluginManager({
      hostApiContext: pluginsHostAPIContext,
      platformContext: context,
      hostApiMethods: this,
      documentServicesAPI,
      experimentsAPI,
      resolveHostAPIBySlotRef,
    });
    const compRef = await manager.add({ slotComponentRef, widgetPointer });
    return compRef;
  },
  async uninstallWidgetPlugin(
    context: PlatformContext,
    slotComponentRef: CompRef,
    pluginsHostAPIContext: PluginsHostApiContext,
  ): Promise<void> {
    const manager = createPluginManager({
      hostApiContext: pluginsHostAPIContext,
      platformContext: context,
      hostApiMethods: this,
      documentServicesAPI,
      experimentsAPI,
      resolveHostAPIBySlotRef,
    });
    manager.remove({ slotComponentRef });
  },
  getWidgetPointer(slotPlaceholderCompRef: CompRef): WidgetPointer | undefined {
    return resolveHostAPIBySlotRef(slotPlaceholderCompRef).getWidgetPointer(
      documentServicesAPI,
      slotPlaceholderCompRef,
    );
  },
  getSlotPlaceholderRefByContentRef(compRef: CompRef): CompRef | undefined {
    if (!this.isPluginComponent(compRef)) {
      return undefined;
    }
    return this.resolveHostAPIByWidgetPluginRef(
      compRef,
    ).getSlotPlaceholderRefByContentRef(documentServicesAPI, compRef);
  },
  getSlotPlaceholderDisplayName(slotCompRef: CompRef): string | undefined {
    return resolveHostAPIBySlotRef(slotCompRef).getSlotPlaceholderDisplayName(
      documentServicesAPI,
      slotCompRef,
    );
  },
  getWidgetHostRefBySlotRef(slotCompRef: CompRef): CompRef {
    return resolveHostAPIBySlotRef(slotCompRef).getWidgetHostRef(
      documentServicesAPI,
      slotCompRef,
    );
  },
  getWidgetHostRefByWidgetRef(compRef: CompRef): CompRef {
    return this.resolveHostAPIByWidgetPluginRef(compRef).getWidgetHostRef(
      documentServicesAPI,
      compRef,
    );
  },
  getSlotContentRef(compRef: CompRef): CompRef | undefined {
    return resolveHostAPIBySlotRef(compRef).getSlotContentRef(
      documentServicesAPI,
      compRef,
    );
  },
  getSlotPosition(compRef: CompRef) {
    return resolveHostAPIBySlotRef(compRef).getSlotPosition(
      documentServicesAPI,
      compRef,
    );
  },
  getCompRefForSlotSelection(compRef: CompRef) {
    return resolveHostAPIBySlotRef(compRef).getCompRefForSlotSelection(
      documentServicesAPI,
      compRef,
    );
  },
  hasWidgetSlot(widgetCompRef: CompRef): boolean {
    return this.resolveHostAPIByWidgetCompRef(widgetCompRef).hasWidgetSlot(
      documentServicesAPI,
      widgetCompRef,
    );
  },
  getSlotIdInBlocksAppEditor(slotCompRef: CompRef): string {
    return documentServicesAPI.platform.controllers.connections.getPrimaryConnection(
      slotCompRef,
    )?.role;
  },
  getPlacementInBlocksAppEditor(
    slotId: string,
  ): WidgetSlot['placement'] | undefined {
    const widgetPointer =
      documentServicesAPI.appStudio?.widgets.getByRootCompId(
        documentServicesAPI.pages.getCurrentPageId(),
      );
    if (!widgetPointer) {
      return undefined;
    }
    const devCenterWidgetId = documentServicesAPI.data.getById(
      widgetPointer?.id,
    )?.devCenterWidgetId;

    return {
      appDefinitionId: window.appStudioModel?.devSiteAppDefId,
      widgetId: devCenterWidgetId,
      slotId,
    };
  },
  getSlotInBlocksAppEditor(slotCompRef: CompRef): WidgetSlot | undefined {
    const slotData =
      documentServicesAPI.components.slots.getSlotsData(slotCompRef).slot;
    if (!slotData) {
      return undefined;
    }

    const slotId = this.getSlotIdInBlocksAppEditor(slotCompRef);
    const placement = this.getPlacementInBlocksAppEditor(slotId);
    const slotComponentData: DsItem | undefined =
      documentServicesAPI.components.data.get(slotData);

    if (!placement) {
      return undefined;
    }

    return {
      compRef: slotCompRef,
      role: slotId,
      pluginInfo: getPluginInfo(documentServicesAPI, slotComponentData),
      placement,
    };
  },
  getAvailableWidgetSlotsToMoveContent(
    slotComponentRef: CompRef,
  ): Pick<WidgetSlotToMoveContent, 'slotRef' | 'name'>[] {
    const hostCompRef = this.getWidgetHostRefBySlotRef(slotComponentRef);
    if (!hostCompRef) {
      return [];
    }

    const widgetPluginPointer = this.getWidgetPointer(slotComponentRef);
    const widgetSlots = this.getWidgetSlots(hostCompRef);
    const marketData = widgetPluginPointer
      ? getPluginMarketData(documentServicesAPI, widgetPluginPointer)
      : undefined;

    const currentPluginPlacements = marketData?.placements ?? [];

    const availableSlots = widgetSlots.filter((widgetSlot) => {
      if (this.isWidgetSlotPopulated(widgetSlot.compRef)) {
        return false;
      }
      return find(currentPluginPlacements, widgetSlot.placement);
    });

    return availableSlots.map((slot) => ({
      slotRef: slot.compRef,
      name: this.getSlotPlaceholderDisplayName(slot.compRef),
    }));
  },
  getWidgetSlotsToMoveContent(
    slotComponentRef: CompRef,
  ): WidgetSlotToMoveContent[] {
    const hostCompRef = this.getWidgetHostRefBySlotRef(slotComponentRef);
    if (!hostCompRef) {
      return [];
    }

    const widgetPluginPointer = this.getWidgetPointer(slotComponentRef);
    const widgetSlots = this.getWidgetSlots(hostCompRef);
    const marketData = widgetPluginPointer
      ? getPluginMarketData(documentServicesAPI, widgetPluginPointer)
      : undefined;

    const currentPluginPlacements = marketData?.placements ?? [];
    const currentSlotPlacement = this.getWidgetSlotPlacement(slotComponentRef);

    const slotsToMove = widgetSlots.filter((widgetSlot) => {
      return find(currentPluginPlacements, widgetSlot.placement);
    });

    return slotsToMove.map((slot) => ({
      slotRef: slot.compRef,
      pluginInfo: slot.pluginInfo,
      name: this.getSlotPlaceholderDisplayName(slot.compRef),
      isPopulated: this.isWidgetSlotPopulated(slot.compRef),
      isSelected: isEqual(currentSlotPlacement, slot.placement),
    }));
  },
  isSlotContentMovable(slotComponentRef: CompRef): boolean {
    return (
      this.isWidgetSlotPopulated(slotComponentRef) &&
      this.getAvailableWidgetSlotsToMoveContent(slotComponentRef).length > 0
    );
  },
  isSlotContentPotentialMovable(slotComponentRef: CompRef): boolean {
    return (
      this.isWidgetSlotPopulated(slotComponentRef) &&
      this.getWidgetSlotsToMoveContent(slotComponentRef).length > 1
    );
  },
  async moveToSlot(
    context: PlatformContext,
    fromSlotComponentRef: CompRef,
    toSlotComponentRef: CompRef,
    pluginsHostAPIContext: PluginsHostApiContext,
  ) {
    const currentWidgetPluginPointer =
      this.getWidgetPointer(fromSlotComponentRef);

    if (!currentWidgetPluginPointer) {
      return;
    }

    await this.installWidgetPlugin(
      context,
      toSlotComponentRef,
      currentWidgetPluginPointer,
      pluginsHostAPIContext,
    );
    await this.uninstallWidgetPlugin(
      context,
      fromSlotComponentRef,
      pluginsHostAPIContext,
    );
    await documentServicesAPI.waitForChangesAppliedAsync();
  },
  async highlightSlot(compRef: CompRef) {
    resolveHostAPIBySlotRef(compRef).highlightSlot(
      documentServicesAPI,
      compRef,
    );
  },
  unhighlightSlot(compRef: CompRef) {
    resolveHostAPIBySlotRef(compRef).unhighlightSlot(
      documentServicesAPI,
      compRef,
    );
  },
  isWidgetSlotPopulated(virtualSlotPlaceholderRef: CompRef): boolean {
    return Boolean(this.getSlotContentRef(virtualSlotPlaceholderRef));
  },
  isPluginComponent(widgetPluginCompRef: CompRef): boolean {
    const componentType = this.getComponentType(widgetPluginCompRef);
    if (!SUPPORTED_PLUGIN_COMPONENT_TYPES.includes(componentType)) {
      return false;
    }

    return (
      tpaHostAPI.isPluginComponent(documentServicesAPI, widgetPluginCompRef) ||
      blocksHostAPI.isPluginComponent(documentServicesAPI, widgetPluginCompRef)
    );
  },
  async showOnPage(compRef: CompRef) {
    const placement = this.getWidgetSlotPlacement(compRef);

    if (
      !placement.appDefinitionId ||
      !placement.widgetId ||
      !placement.slotId
    ) {
      console.error(
        `Plugins Host API: 'showOnPage' missing placement params for ${placement}`,
      );
      return this.highlightSlot(compRef);
    }

    const hostComponentRef = this.getWidgetHostRefBySlotRef(compRef);
    const appData = documentServicesAPI.platform.getAppDataByAppDefId(
      placement.appDefinitionId,
    );

    if (!appData?.applicationId) {
      console.error(
        `Plugins Host API: 'showOnPage' applicationId not found for ${placement}`,
      );
      return this.highlightSlot(compRef);
    }

    documentServicesAPI.platform.notifyApplication(
      appData?.applicationId,
      platformEvents.factory.widgetPluginShowOnPageClicked({
        hostComponentRef,
        placement: {
          appDefinitionId: placement.appDefinitionId,
          widgetId: placement.widgetId,
          slotId: placement.slotId,
        },
      }),
    );

    return this.highlightSlot(compRef);
  },
});
